import { useEffect, useState, useCallback } from "react";
import { api, BACKEND_URL, isSuperAdmin } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Trash2, FileText, Eye, Send, Download, Loader2, Pencil, ChevronDown, CheckCircle2, X,
} from "lucide-react";

const PAYMENT_PRESETS = [
  "Bonifico anticipato 50% + saldo alla consegna",
  "Bonifico anticipato 100%",
  "Pagamento alla consegna (contrassegno)",
  "Bonifico a 30 giorni data fattura",
  "Personalizzato",
];

const DEFAULT_BANK_DETAILS = `Banca: ___
IBAN: IT__ ____ ____ ____ ____ ____ ___
Intestato a: Officina Franco Srl`;

const STATUS_LABELS = {
  draft: "Bozza",
  sent: "Inviato",
  accepted: "Accettato",
  rejected: "Rifiutato",
};

const STATUS_COLORS = {
  draft: "bg-slate-50 text-slate-700 border-slate-200",
  sent: "bg-blue-50 text-blue-700 border-blue-200",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

const fmtEur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const EMPTY_FORM = (quote) => ({
  customer_name: quote.name,
  customer_phone: quote.phone,
  customer_email: quote.email || "",
  vehicle_ref: [quote.brand_model, quote.engine_code].filter(Boolean).join(" · "),
  items: [
    { description: quote.part_type === "motore" ? "Motore usato/revisionato" : "Cambio usato/revisionato", qty: 1, unit_price: 0 },
  ],
  discount_type: "percent",
  discount_value: 0,
  vat_percent: 22,
  payment_terms: PAYMENT_PRESETS[0],
  payment_terms_custom: "",
  bank_details: DEFAULT_BANK_DETAILS,
  tech_notes: "",
  validity_days: 30,
});

export const QuotationPanel = ({ quote }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | quotationObject

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/quotations", { params: { quote_id: quote.id } });
      setList(data);
    } finally {
      setLoading(false);
    }
  }, [quote.id]);

  useEffect(() => { reload(); }, [reload]);

  if (editing) {
    return (
      <QuotationEditor
        quote={quote}
        initial={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => { reload(); setEditing(null); }}
        onUpdated={() => reload()}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          Compila il preventivo per <strong>{quote.name}</strong>, genera il PDF e invialo direttamente via email al cliente.
        </p>
        <Button
          onClick={() => setEditing("new")}
          data-testid="new-quotation-btn"
          className="rounded-none bg-red-600 hover:bg-red-700 text-xs tracking-[0.2em] uppercase"
        >
          <Plus className="w-3.5 h-3.5 mr-2" /> Nuovo preventivo
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-500 text-sm">Caricamento preventivi...</div>
      ) : list.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-10 text-center text-slate-500">
          <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          Nessun preventivo ancora generato per questa richiesta.
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((q) => (
            <QuotationRow
              key={q.id}
              quotation={q}
              onEdit={() => setEditing(q)}
              onReload={reload}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const QuotationRow = ({ quotation: q, onEdit, onReload }) => {
  const [sending, setSending] = useState(false);

  const pdfUrl = `${BACKEND_URL}/api/admin/quotations/${q.id}/pdf`;
  const openPdf = async () => {
    const token = localStorage.getItem("admin_token");
    const res = await fetch(pdfUrl, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Impossibile aprire il PDF"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const sendEmail = async () => {
    if (!q.customer_email) { toast.error("Cliente senza email — non posso inviare"); return; }
    if (!window.confirm(`Inviare il preventivo ${q.number} a ${q.customer_email}?`)) return;
    setSending(true);
    try {
      await api.post(`/admin/quotations/${q.id}/send`);
      toast.success(`Preventivo inviato a ${q.customer_email}`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio fallito");
    } finally {
      setSending(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare il preventivo ${q.number}?`)) return;
    try {
      await api.delete(`/admin/quotations/${q.id}`);
      toast.success("Eliminato");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div data-testid={`quotation-row-${q.id}`} className="border border-slate-200 bg-white p-4 hover:border-slate-400 transition-colors">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <FileText className="w-5 h-5 text-blue-900" />
          <div>
            <div className="font-mono text-sm font-semibold text-slate-900">{q.number}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {fmtDate(q.date)} · {q.items.length} {q.items.length === 1 ? "articolo" : "articoli"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="font-serif-display text-2xl text-slate-900">{fmtEur(q.totals?.total)}</div>
            <div className={`inline-block text-[10px] tracking-wider uppercase border px-2 py-0.5 mt-1 ${STATUS_COLORS[q.status]}`}>
              {STATUS_LABELS[q.status]}
              {q.sent_at && q.status === "sent" && ` · ${fmtDate(q.sent_at)}`}
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap mt-4 pt-3 border-t border-slate-100">
        <Button onClick={openPdf} variant="outline" size="sm" className="rounded-none border-slate-300 text-xs tracking-[0.15em] uppercase" data-testid={`pdf-preview-${q.id}`}>
          <Eye className="w-3.5 h-3.5 mr-2" /> Anteprima PDF
        </Button>
        <Button onClick={sendEmail} disabled={sending || !q.customer_email} size="sm" className="rounded-none bg-blue-900 hover:bg-blue-800 text-xs tracking-[0.15em] uppercase" data-testid={`pdf-send-${q.id}`}>
          {sending ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-2" />}
          {q.pdf_sent_count > 0 ? `Reinvia (${q.pdf_sent_count}x)` : "Invia al cliente"}
        </Button>
        <Button onClick={onEdit} variant="outline" size="sm" className="rounded-none border-slate-300 text-xs tracking-[0.15em] uppercase" data-testid={`pdf-edit-${q.id}`}>
          <Pencil className="w-3.5 h-3.5 mr-2" /> Modifica
        </Button>
        {isSuperAdmin() && (
          <Button onClick={remove} variant="ghost" size="sm" className="rounded-none text-red-600 hover:bg-red-50 hover:text-red-700 text-xs tracking-[0.15em] uppercase ml-auto" data-testid={`pdf-delete-${q.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

const QuotationEditor = ({ quote, initial, onClose, onSaved, onUpdated }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => {
    if (initial) {
      const isPreset = PAYMENT_PRESETS.includes(initial.payment_terms);
      return {
        customer_name: initial.customer_name,
        customer_phone: initial.customer_phone,
        customer_email: initial.customer_email || "",
        vehicle_ref: initial.vehicle_ref || "",
        items: initial.items.length ? initial.items : EMPTY_FORM(quote).items,
        discount_type: initial.discount_type,
        discount_value: initial.discount_value,
        vat_percent: initial.vat_percent,
        payment_terms: isPreset ? initial.payment_terms : "Personalizzato",
        payment_terms_custom: isPreset ? "" : initial.payment_terms,
        bank_details: initial.bank_details || DEFAULT_BANK_DETAILS,
        tech_notes: initial.tech_notes || "",
        validity_days: initial.validity_days || 30,
      };
    }
    return EMPTY_FORM(quote);
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const updateItem = (i, k, v) =>
    setForm((p) => ({ ...p, items: p.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addItem = () => setForm((p) => ({ ...p, items: [...p.items, { description: "", qty: 1, unit_price: 0 }] }));
  const removeItem = (i) => setForm((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));

  // Live totals
  const subtotal = form.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const discountAmount = form.discount_type === "percent"
    ? subtotal * (Number(form.discount_value) || 0) / 100
    : Math.min(Number(form.discount_value) || 0, subtotal);
  const net = subtotal - discountAmount;
  const vatAmount = net * (Number(form.vat_percent) || 0) / 100;
  const total = net + vatAmount;

  const buildPayload = () => ({
    quote_id: quote.id,
    customer_name: form.customer_name,
    customer_phone: form.customer_phone,
    customer_email: form.customer_email,
    vehicle_ref: form.vehicle_ref,
    items: form.items.map((it) => ({
      description: it.description,
      qty: Number(it.qty) || 0,
      unit_price: Number(it.unit_price) || 0,
    })),
    discount_type: form.discount_type,
    discount_value: Number(form.discount_value) || 0,
    vat_percent: Number(form.vat_percent) || 0,
    payment_terms: form.payment_terms === "Personalizzato" ? form.payment_terms_custom : form.payment_terms,
    bank_details: form.bank_details,
    tech_notes: form.tech_notes,
    validity_days: Number(form.validity_days) || 30,
  });

  const validate = () => {
    if (!form.customer_name || !form.customer_phone) {
      toast.error("Nome e telefono cliente obbligatori"); return false;
    }
    const validItems = form.items.filter((it) => it.description?.trim());
    if (validItems.length === 0) { toast.error("Aggiungi almeno un articolo"); return false; }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await api.patch(`/admin/quotations/${initial.id}`, payload);
        toast.success("Preventivo aggiornato");
        onUpdated();
        onClose();
      } else {
        await api.post("/admin/quotations", payload);
        toast.success("Preventivo creato come bozza");
        onSaved();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const saveAndPreview = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      let id;
      if (isEdit) {
        await api.patch(`/admin/quotations/${initial.id}`, payload);
        id = initial.id;
      } else {
        const { data } = await api.post("/admin/quotations", payload);
        id = data.id;
        onSaved();
        toast.success("Bozza salvata, apro anteprima PDF");
      }
      const token = localStorage.getItem("admin_token");
      const res = await fetch(`${BACKEND_URL}/api/admin/quotations/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Errore");
    } finally {
      setSaving(false);
    }
  };

  const saveAndSend = async () => {
    if (!validate()) return;
    if (!form.customer_email) { toast.error("Email cliente mancante"); return; }
    if (!window.confirm(`Inviare il preventivo a ${form.customer_email}?`)) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      let id;
      if (isEdit) {
        await api.patch(`/admin/quotations/${initial.id}`, payload);
        id = initial.id;
      } else {
        const { data } = await api.post("/admin/quotations", payload);
        id = data.id;
      }
      await api.post(`/admin/quotations/${id}/send`);
      toast.success(`Preventivo inviato a ${form.customer_email}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="quotation-editor" className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
      <div className="flex items-center justify-between sticky top-0 bg-white py-2 z-10 border-b border-slate-200">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-blue-900">
            {isEdit ? `Modifica ${initial.number}` : "Nuovo preventivo"}
          </div>
          <div className="font-serif-display text-xl text-slate-900">
            {form.customer_name || "Cliente"}
          </div>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="cancel-quotation">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Cliente */}
      <Section title="Dati cliente">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FF label="Nome e Cognome *">
            <Input value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} className="rounded-none border-slate-300" data-testid="q-customer-name" />
          </FF>
          <FF label="Telefono *">
            <Input value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} className="rounded-none border-slate-300" data-testid="q-customer-phone" />
          </FF>
          <FF label="Email" full>
            <Input type="email" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} placeholder="Necessaria per invio automatico" className="rounded-none border-slate-300" data-testid="q-customer-email" />
          </FF>
          <FF label="Riferimento veicolo" full>
            <Input value={form.vehicle_ref} onChange={(e) => update("vehicle_ref", e.target.value)} placeholder="es. Mercedes E220 2013 - codice OM651" className="rounded-none border-slate-300" data-testid="q-vehicle-ref" />
          </FF>
        </div>
      </Section>

      {/* Articoli */}
      <Section title="Articoli del preventivo">
        <div className="space-y-2">
          {form.items.map((it, i) => (
            <div key={i} data-testid={`item-row-${i}`} className="grid grid-cols-12 gap-2 items-start">
              <div className="col-span-7">
                <Textarea
                  value={it.description}
                  onChange={(e) => updateItem(i, "description", e.target.value)}
                  rows={2}
                  placeholder="Descrizione articolo"
                  className="rounded-none border-slate-300 text-sm"
                  data-testid={`item-desc-${i}`}
                />
              </div>
              <div className="col-span-2">
                <Input type="number" step="0.5" min="0" value={it.qty} onChange={(e) => updateItem(i, "qty", e.target.value)} placeholder="Q.tà" className="rounded-none border-slate-300 text-sm" data-testid={`item-qty-${i}`} />
              </div>
              <div className="col-span-2">
                <Input type="number" step="0.01" min="0" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} placeholder="€ unit." className="rounded-none border-slate-300 text-sm" data-testid={`item-price-${i}`} />
              </div>
              <div className="col-span-1 flex justify-end pt-1">
                {form.items.length > 1 && (
                  <button onClick={() => removeItem(i)} className="text-slate-400 hover:text-red-600" data-testid={`item-remove-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="col-span-12 text-right text-xs text-slate-500 font-mono">
                Totale riga: <strong className="text-slate-900">{fmtEur((Number(it.qty) || 0) * (Number(it.unit_price) || 0))}</strong>
              </div>
            </div>
          ))}
        </div>
        <Button onClick={addItem} variant="outline" size="sm" className="mt-3 rounded-none border-slate-300 text-xs tracking-[0.15em] uppercase" data-testid="add-item">
          <Plus className="w-3.5 h-3.5 mr-2" /> Aggiungi articolo
        </Button>
      </Section>

      {/* Calcoli */}
      <Section title="Sconto, IVA, validità">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <FF label="Tipo sconto">
            <Select value={form.discount_type} onValueChange={(v) => update("discount_type", v)}>
              <SelectTrigger className="rounded-none border-slate-300" data-testid="q-discount-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">Percentuale (%)</SelectItem>
                <SelectItem value="euro">Importo fisso (€)</SelectItem>
              </SelectContent>
            </Select>
          </FF>
          <FF label="Valore sconto">
            <Input type="number" step="0.01" min="0" value={form.discount_value} onChange={(e) => update("discount_value", e.target.value)} className="rounded-none border-slate-300" data-testid="q-discount-value" />
          </FF>
          <FF label="IVA (%)">
            <Input type="number" step="0.01" min="0" value={form.vat_percent} onChange={(e) => update("vat_percent", e.target.value)} className="rounded-none border-slate-300" data-testid="q-vat" />
          </FF>
          <FF label="Validità (giorni)">
            <Input type="number" min="1" value={form.validity_days} onChange={(e) => update("validity_days", e.target.value)} className="rounded-none border-slate-300" data-testid="q-validity" />
          </FF>
        </div>

        {/* Live totals preview */}
        <div className="mt-4 bg-slate-50 border border-slate-200 p-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-blue-900 mb-2">Riepilogo (in tempo reale)</div>
          <div className="space-y-1 text-sm">
            <Row label="Imponibile" value={fmtEur(subtotal)} />
            {discountAmount > 0 && <Row label={`Sconto ${form.discount_type === "percent" ? form.discount_value + "%" : ""}`} value={`− ${fmtEur(discountAmount)}`} />}
            <Row label="Netto" value={fmtEur(net)} />
            <Row label={`IVA ${form.vat_percent}%`} value={`+ ${fmtEur(vatAmount)}`} />
            <div className="border-t border-slate-300 pt-2 mt-2">
              <Row label="TOTALE" value={fmtEur(total)} big />
            </div>
          </div>
        </div>
      </Section>

      {/* Pagamento */}
      <Section title="Modalità di pagamento">
        <FF label="Scegli modalità" full>
          <Select value={form.payment_terms} onValueChange={(v) => update("payment_terms", v)}>
            <SelectTrigger className="rounded-none border-slate-300" data-testid="q-payment-preset"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAYMENT_PRESETS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </FF>
        {form.payment_terms === "Personalizzato" && (
          <FF label="Testo personalizzato pagamento" full>
            <Textarea rows={2} value={form.payment_terms_custom} onChange={(e) => update("payment_terms_custom", e.target.value)} className="rounded-none border-slate-300" data-testid="q-payment-custom" />
          </FF>
        )}
      </Section>

      {/* Coordinate bancarie */}
      <Section title="Coordinate bancarie (per bonifico)">
        <Textarea rows={4} value={form.bank_details} onChange={(e) => update("bank_details", e.target.value)} className="rounded-none border-slate-300 font-mono text-sm" data-testid="q-bank" />
      </Section>

      {/* Note */}
      <Section title="Note aggiuntive (opzionali)">
        <Textarea rows={3} value={form.tech_notes} onChange={(e) => update("tech_notes", e.target.value)} placeholder="Condizioni, esclusioni, tempi di consegna, ecc." className="rounded-none border-slate-300" data-testid="q-notes" />
      </Section>

      {/* Actions */}
      <div className="sticky bottom-0 bg-white border-t border-slate-200 pt-4 -mx-1 px-1">
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={saving} variant="outline" className="rounded-none border-slate-300 text-xs tracking-[0.2em] uppercase" data-testid="save-quotation">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-2" />}
            {isEdit ? "Salva modifiche" : "Salva bozza"}
          </Button>
          <Button onClick={saveAndPreview} disabled={saving} className="rounded-none bg-slate-900 hover:bg-slate-800 text-xs tracking-[0.2em] uppercase" data-testid="preview-quotation">
            <Eye className="w-3.5 h-3.5 mr-2" /> Anteprima PDF
          </Button>
          <Button onClick={saveAndSend} disabled={saving || !form.customer_email} className="rounded-none bg-red-600 hover:bg-red-700 text-xs tracking-[0.2em] uppercase ml-auto" data-testid="send-quotation">
            <Send className="w-3.5 h-3.5 mr-2" /> Salva e invia al cliente
          </Button>
        </div>
        {!form.customer_email && (
          <p className="text-xs text-amber-700 mt-2">⚠️ Aggiungi un&apos;email cliente per poter inviare automaticamente il preventivo.</p>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="text-[10px] tracking-[0.25em] uppercase text-blue-900 border-b border-slate-200 pb-2 mb-3">
      {title}
    </div>
    {children}
  </div>
);

const FF = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
    <Label className="text-[10px] tracking-[0.25em] uppercase text-slate-500">{label}</Label>
    {children}
  </div>
);

const Row = ({ label, value, big }) => (
  <div className={`flex justify-between ${big ? "font-serif-display text-xl text-slate-900" : "text-slate-700"}`}>
    <span>{label}</span>
    <span className={big ? "font-bold text-red-600" : "font-mono"}>{value}</span>
  </div>
);
